import { shouldIgnoreKeyEvent } from "../utils/keyboard";
import { throttle, throttleKeyDown } from "../utils/throttle";
import { serveDevTools } from "./webrtc/devtools";
import { MetricsPoller } from "./webrtc/stats";
import { expandIceServers } from "./webrtc/util";

const LOOKUP = {
  " ": "ok",
  Enter: "ok",
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
  p: "power",
  h: "home",
  Escape: "back",
  b: "back",
  x: "input",
  i: "info",
  s: "settings",
  "+": "volume_up",
  "-": "volume_down",
  M: "mute",
  0: "number-0",
  1: "number-1",
  2: "number-2",
  3: "number-3",
  4: "number-4",
  5: "number-5",
  6: "number-6",
  7: "number-7",
  8: "number-8",
  9: "number-9",
  ">": "ch_next",
  "<": "ch_prev",
};

const FAILED_STATES = ["closed", "failed"];
const REMOTE_CONTROL_KEYBOARD_PASS_THROUGH = "keyboard_pass_through";
const SEND_KEY_PRESS_EVENT_THROTTLE_MS = 200; // Should be larger than IR transmission time
const SEND_KEYBOARD_PASS_THROUGH_EVENT_THROTTLE_MS = 20;
const POINTER_FLUSH_INTERVAL_MS = 100;

function retriggerAnimation(elementId, className) {
  const el = document.getElementById(elementId);
  if (!el) return;
  el.classList.remove(className);
  void el.offsetWidth;
  el.classList.add(className);
}

// Animate a clone of the captured frame from the TV screen into the Screenshots
// activity-bar tab so the user gets a visceral "screenshot landed in the gallery" feel.
function flyScreenshotToGallery(srcUrl) {
  const source = document.getElementById("device-stream-video");
  const target = document.getElementById("activity-tab-screenshots-button");
  if (!source || !target || !srcUrl) return;

  const sRect = source.getBoundingClientRect();
  const tRect = target.getBoundingClientRect();
  if (sRect.width === 0 || sRect.height === 0) return;

  const img = document.createElement("img");
  img.src = srcUrl;
  img.alt = "";
  img.style.cssText = `
    position: fixed;
    z-index: 60;
    pointer-events: none;
    left: ${sRect.left}px;
    top: ${sRect.top}px;
    width: ${sRect.width}px;
    height: ${sRect.height}px;
    border-radius: 4px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
    will-change: transform, opacity;
  `;
  document.body.appendChild(img);

  const dx = tRect.left + tRect.width / 2 - (sRect.left + sRect.width / 2);
  const dy = tRect.top + tRect.height / 2 - (sRect.top + sRect.height / 2);
  const scale = Math.max(tRect.width / sRect.width, 0.04);

  const animation = img.animate(
    [
      { transform: "translate(0, 0) scale(1)", opacity: 1 },
      { transform: `translate(${dx}px, ${dy}px) scale(${scale})`, opacity: 0.2 },
    ],
    { duration: 750, easing: "cubic-bezier(0.5, 0, 0.2, 1)", fill: "forwards" },
  );
  const cleanup = () => {
    img.remove();
    retriggerAnimation("activity-tab-screenshots-icon", "animate-tab-pulse");
  };
  animation.onfinish = cleanup;
  animation.oncancel = cleanup;
}

// Snapshot the current device-stream video frame to a data URL so we can animate
// what the user just saw without waiting on the upload round-trip. Returns null if
// the video isn't ready or the canvas is tainted (cross-origin frame from CloudFront).
function captureVideoFrameDataUrl() {
  const video = document.getElementById("device-stream-video");
  if (!video || !video.videoWidth || !video.videoHeight) return null;
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  try {
    canvas.getContext("2d").drawImage(video, 0, 0);
    return canvas.toDataURL("image/png");
  } catch (e) {
    console.warn("Failed to capture video frame", e);
    return null;
  }
}

// Apply each space-separated class from data-active-class-name to the element.
function addActiveClasses($el) {
  const value = $el.dataset["activeClassName"];
  if (!value) return;
  value
    .split(/\s+/)
    .filter(Boolean)
    .forEach((cls) => $el.classList.add(cls));
}

function removeActiveClasses($el) {
  const value = $el.dataset["activeClassName"];
  if (!value) return;
  value
    .split(/\s+/)
    .filter(Boolean)
    .forEach((cls) => $el.classList.remove(cls));
}

export const Webrtc = {
  async mounted() {
    // Initialize throttle functions based on long_press_support feature flag
    this.longPressSupport = this.el.dataset.longPressSupport === "true";
    const throttleFn = this.longPressSupport ? throttleKeyDown : throttle;

    this.sendKeyPressEventThrottled = throttleFn((event, command) => {
      this.sendRemoteControl(command, event, event.repeat);
    }, SEND_KEY_PRESS_EVENT_THROTTLE_MS);

    this.sendKeyboardPassThroughEventThrottled = throttleFn((event) => {
      this.sendRemoteControl(REMOTE_CONTROL_KEYBOARD_PASS_THROUGH, event, event.repeat);
    }, SEND_KEYBOARD_PASS_THROUGH_EVENT_THROTTLE_MS);

    this.bindEventFunctions();

    this.videoElement = this.el.querySelector("#device-stream-video");
    this.audioElement = this.el.querySelector("#device-stream-audio");

    this.audioUnlocked = false;
    this.desiredVolume = null;
    this.warmedUp = false;

    this.cleanupEventListeners = null;
    this.bufferedIceCandidates = [];
    this.keyEventSeqNo = 0;
    this.keyboardPassThrough = false;
    this.pointerControl = false;
    this.pointerRightClick = false;
    this.pointerScroll = false;
    this.pointerFlushTimer = null;
    this.pointerDeltaX = 0;
    this.pointerDeltaY = 0;
    this.pointerScrollY = 0;
    this.devtools = serveDevTools(
      () => this.peer,
      () => this.el.dataset.sessionId,
    );

    const iceServers = expandIceServers(JSON.parse(this.el.dataset.iceServers));
    console.log(
      "ice servers",
      iceServers.map(({ urls }) => urls),
    );

    this.handleEvent("start_webrtc", async (data) => {
      console.log("Starting WebRTC connection:", data);

      // Remove any existing event handlers in the case that multiple start_webrtc events are received
      this.videoElement.removeEventListener("playing", this.handleVideoPlaying);
      this.removeEventListeners?.();
      this.removeServerEventHandlers?.();

      this.videoStream = null;
      this.audioStream = null;

      this.devtools.closeAll();
      this.peer = new RTCPeerConnection({
        // uncomment to test TURN relay
        // iceTransportPolicy: "relay",
        bundlePolicy: "max-bundle",
        iceServers,
      });

      this.videoElement.addEventListener("playing", this.handleVideoPlaying);

      this.peer.addEventListener("track", (event) => {
        console.log("Received track event", event.track.kind, event);

        if (event.track.kind === "video") {
          this.videoStream = event.streams[0];
        }

        if (event.track.kind === "audio") {
          this.audioStream = event.streams[0];
        }
      });

      this.peer.addEventListener("connectionstatechange", async (event) => {
        console.log("Connection state changed", this.peer.connectionState, event);

        if (this.peer.connectionState === "connected") {
          console.log("Connected. Assigning stream to player");

          this.videoElement.srcObject = this.videoStream;
          this.audioElement.srcObject = this.audioStream;
          this.applyAudioState();

          this.metrics = new MetricsPoller(this.peer, (stats) => this.writeStats(stats));
        }

        /*
         * TODO: This needs more work as it reconnects under relay.
         */
        if (this.peer.connectionState === "disconnected") {
          console.log("restarting ice");

          this.peer.restartIce();
        }

        if (FAILED_STATES.includes(this.peer.connectionState) && this.metrics) {
          this.metrics.destroy();
          this.metrics = null;
        }

        this.safePushEvent("connection:changed", {
          state: this.peer.connectionState,
        });
      });

      this.peer.addEventListener("icecandidate", async (event) => {
        if (event.candidate) {
          console.log("Sending ice candidate", event.candidate);
          this.safePushEvent("send_ice_candidate", event.candidate);
        }
      });

      this.peer.addEventListener("icecandidateerror", (event) => {
        console.error("Ice candidate error", event);
      });

      this.peer.addEventListener("icegatheringstatechange", (event) => {
        console.log("Ice gathering state change", event);
      });

      this.peer.addEventListener("datachannel", (event) => {
        console.log("Remote has created a data channel", event.channel.label, event);

        this.remoteControl = event.channel;

        // Share the data channel with other hooks that need it
        window.sauronRemoteControl = this.remoteControl;

        // Dispatch an event to notify other components that the channel is ready
        window.dispatchEvent(
          new CustomEvent("remoteControlReady", {
            detail: { remoteControl: this.remoteControl },
          }),
        );

        this.remoteControl.addEventListener("message", (message) =>
          console.log("Received data channel messsage", message),
        );

        this.safePushEvent("data_channel", { status: "open" });
      });

      const sidecarOfferHandle = this.handleEvent("sidecar_offer", async (offer) => {
        console.log("Received sidecar offer", offer);

        await this.handleOffer(offer);
      });

      const iceCandidateHandle = this.handleEvent("ice_candidate", async (candidate) => {
        try {
          await this.receiveIceCandidate(candidate);
        } catch (e) {
          console.error("Failed to add ice candidate", candidate, e);
        }
      });

      const captureScreenHandle = this.handleEvent("capture_screen", () => {
        console.log("Capturing screen");
        this.remoteControl.send(JSON.stringify({ type: "capture_screen" }));
        retriggerAnimation("screen-flash-overlay", "animate-screen-flash");
        retriggerAnimation("screen-capture-icon", "animate-camera-illuminate");

        const dataUrl = captureVideoFrameDataUrl();
        this.lastCapturedFrameDataUrl = dataUrl;
        if (dataUrl) flyScreenshotToGallery(dataUrl);
      });

      // Fallback: if the local canvas capture failed (e.g. cross-origin video tainting
      // from CloudFront in prod), play the fly animation against the server-uploaded URL.
      const screenshotCapturedHandle = this.handleEvent("screenshot_captured", ({ url }) => {
        if (this.lastCapturedFrameDataUrl) {
          this.lastCapturedFrameDataUrl = null;
          return;
        }
        if (url) flyScreenshotToGallery(url);
      });

      const setRecordingPausedHandle = this.handleEvent("set_recording_paused", ({ paused }) => {
        this.remoteControl.send(JSON.stringify({ type: "set_recording_paused", paused }));
      });

      const keyboardPassThroughHandle = this.handleEvent("keyboard_pass_through", ({ enabled }) => {
        this.keyboardPassThrough = enabled;
      });

      const pointerControlHandle = this.handleEvent(
        "pointer_control",
        ({ enabled, right_click, scroll }) => {
          this.pointerControl = enabled;
          this.pointerRightClick = right_click;
          this.pointerScroll = scroll;
          if (!enabled && this.pointerLocked()) document.exitPointerLock();
        },
      );

      const setupEventListenersHandle = this.handleEvent("setup_event_listeners", () => {
        this.setupEventListeners();
        this.warmedUp = true;
        this.applyAudioState();
      });

      const removeEventListenersHandle = this.handleEvent("remove_event_listeners", () => {
        this.removeEventListeners();
      });

      const executeSnippetHandle = this.handleEvent(
        "execute_snippet",
        ({ snippet, ui_xml, snippet_id }) => {
          console.log("Executing code snippet", {
            snippet,
            ui_xml,
            snippet_id,
          });
          this.remoteControl.send(
            JSON.stringify({
              type: "execute_snippet",
              ui_xml,
              snippet,
              snippet_id,
            }),
          );
        },
      );

      const updateUiHandle = this.handleEvent("update_ui", ({ ui_xml }) => {
        console.log("Update UI only", {
          ui_xml,
        });
        this.remoteControl.send(
          JSON.stringify({
            type: "update_ui",
            ui_xml,
          }),
        );
      });

      const sendTextHandle = this.handleEvent("send_text", ({ text }) => {
        if (!this.remoteControlReady()) return;

        this.remoteControl.send(
          JSON.stringify({
            type: "remote_control",
            command: `text ${text}`,
            seq: this.keyEventSeqNo++,
          }),
        );
      });

      const updateAudioHandle = this.handleEvent("update_audio", ({ volume }) => {
        this.updateAudio(volume);
      });

      // Assign immediately so a second start_webrtc that runs while we await
      // handleOffer below still removes these handlers (avoids duplicate capture_screen, etc.)
      this.removeServerEventHandlers = () => {
        this.removeHandleEvent(sidecarOfferHandle);
        this.removeHandleEvent(iceCandidateHandle);
        this.removeHandleEvent(captureScreenHandle);
        this.removeHandleEvent(screenshotCapturedHandle);
        this.removeHandleEvent(setRecordingPausedHandle);
        this.removeHandleEvent(keyboardPassThroughHandle);
        this.removeHandleEvent(pointerControlHandle);
        this.removeHandleEvent(setupEventListenersHandle);
        this.removeHandleEvent(removeEventListenersHandle);
        this.removeHandleEvent(executeSnippetHandle);
        this.removeHandleEvent(updateUiHandle);
        this.removeHandleEvent(sendTextHandle);
        this.removeHandleEvent(updateAudioHandle);
      };

      if (data.offer) {
        await this.handleOffer(data.offer);

        data.ice_candidates.forEach((candidate) => this.addIceCandidate(candidate));
      }

      // Add touch event tracking for swipes
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchStartTime = 0;
      this.isSwiping = false;
    });
  },

  setupEventListeners() {
    this.removeEventListeners();

    this.el.addEventListener("remote_control", this.handleRemoteButtonPress);
    this.el.addEventListener("voice_control", this.handleVoiceControl);

    this.videoElement.addEventListener("click", this.handleScreenTap);
    this.videoElement.addEventListener("mousedown", this.handleMouseDown);
    this.videoElement.addEventListener("mousemove", this.handleMouseMove);
    this.videoElement.addEventListener("mouseup", this.handleMouseUp);
    this.videoElement.addEventListener("mouseleave", this.handleMouseUp);
    this.videoElement.addEventListener("contextmenu", this.handleContextMenu);
    this.videoElement.addEventListener("wheel", this.handleWheel, { passive: false });
    document.addEventListener("pointerlockchange", this.handlePointerLockChange);

    document.addEventListener("keydown", this.handleKeyPress);
    document.addEventListener("keyup", this.handleKeyPress);
    document.addEventListener("executedFromCodeEditor", this.handleExecuteSnippet);

    this._cleanupEventListeners = () => {
      this.el.removeEventListener("remote_control", this.handleRemoteButtonPress);
      this.el.removeEventListener("voice_control", this.handleVoiceControl);

      this.videoElement.removeEventListener("click", this.handleScreenTap);
      this.videoElement.removeEventListener("mousedown", this.handleMouseDown);
      this.videoElement.removeEventListener("mousemove", this.handleMouseMove);
      this.videoElement.removeEventListener("mouseup", this.handleMouseUp);
      this.videoElement.removeEventListener("mouseleave", this.handleMouseUp);
      this.videoElement.removeEventListener("contextmenu", this.handleContextMenu);
      this.videoElement.removeEventListener("wheel", this.handleWheel);
      document.removeEventListener("pointerlockchange", this.handlePointerLockChange);
      if (this.pointerLocked()) document.exitPointerLock();
      this.js().removeAttribute(this.el, "data-pointer-locked");
      clearInterval(this.pointerFlushTimer);
      this.pointerFlushTimer = null;

      document.removeEventListener("keydown", this.handleKeyPress);
      document.removeEventListener("keyup", this.handleKeyPress);
      document.removeEventListener("executedFromCodeEditor", this.handleExecuteSnippet);
    };
  },

  removeEventListeners() {
    this._cleanupEventListeners?.();
  },

  bindEventFunctions() {
    this.handleVideoPlaying = this.handleVideoPlaying.bind(this);
    this.handleScreenTap = this.handleScreenTap.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleExecuteSnippet = this.handleExecuteSnippet.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handlePointerLockChange = this.handlePointerLockChange.bind(this);
    this.handleContextMenu = this.handleContextMenu.bind(this);
    this.handleWheel = this.handleWheel.bind(this);
    this.flushPointer = this.flushPointer.bind(this);
    this.handleRemoteButtonPress = this.handleRemoteButtonPress.bind(this);
    this.handleVoiceControl = this.handleVoiceControl.bind(this);
    this.sendKeyPressEventThrottled = this.sendKeyPressEventThrottled.bind(this);
    this.sendKeyboardPassThroughEventThrottled =
      this.sendKeyboardPassThroughEventThrottled.bind(this);
  },

  async handleOffer(offer) {
    // start_webrtc and sidecar_offer both carry it; answering twice aborts the sidecar.
    if (this.peer.currentRemoteDescription?.sdp === offer.sdp) {
      console.log("Ignoring offer already answered", offer);
      return;
    }

    console.log("Handling remote offer", offer);

    try {
      await this.peer.setRemoteDescription(offer);

      const answer = await this.peer.createAnswer();

      await this.peer.setLocalDescription(answer);

      this.safePushEvent("browser_answer", answer);
    } catch (e) {
      console.error("Failed to answer remote offer", offer, e);

      this.safePushEvent("browser_answer_failed", {
        name: e.name,
        message: e.message,
        signaling_state: this.peer.signalingState,
        answered_offer: Boolean(this.peer.currentRemoteDescription),
      });

      return;
    }

    this.bufferedIceCandidates.forEach(async (candidate) => {
      await this.addIceCandidate(candidate);
    });

    this.bufferedIceCandidates = [];
  },

  async receiveIceCandidate(candidate) {
    if (!this.peer.currentRemoteDescription) {
      console.log("Buffering ice candidate, no remote offer");
      this.bufferedIceCandidates.push(candidate);
      return;
    }

    await this.addIceCandidate(candidate);
  },

  async addIceCandidate(candidate) {
    try {
      console.debug("Adding ice candidate", candidate);
      await this.peer.addIceCandidate(candidate);
    } catch (e) {
      console.error("Failed to add ice candidate", candidate, e);
    }
  },

  reconnected() {
    this.safePushEvent("connection:changed", {
      state: this.peer.connectionState,
    });
  },

  remoteControlReady() {
    return this.remoteControl?.readyState === "open";
  },

  sendRemoteControl(command, event, repeat) {
    if (!this.remoteControlReady()) {
      // the data channel isn't open, don't try to send or
      // record commands that will not be sent
      return;
    }

    let remoteControlPayload = {};
    let telemetryPayload = null;
    if (command === REMOTE_CONTROL_KEYBOARD_PASS_THROUGH) {
      // Ignore modifier keys
      if (["Control", "Meta", "Alt", "Shift"].includes(event.key)) return;

      // Precalculate the modifier mask
      const modifierMask =
        (event.ctrlKey ? 0x01 : 0x00) |
        (event.shiftKey ? 0x02 : 0x00) |
        (event.altKey ? 0x04 : 0x00) |
        (event.metaKey ? 0x08 : 0x00);

      remoteControlPayload = {
        type: "remote_control",
        command,
        keyType: event.type,
        key: event.key,
        modifierMask,
        seq: this.keyEventSeqNo++,
      };
    } else {
      console.log(`Sending remote control event "${command}" (${event.type}, repeat: ${repeat})`);
      remoteControlPayload = {
        type: "remote_control",
        command,
        event: event.type,
        repeat,
        seq: this.keyEventSeqNo++,
      };
      telemetryPayload = {
        command,
        event: event.type,
        repeat,
        client_timestamp: performance.now(),
      };
    }

    this.remoteControl.send(JSON.stringify(remoteControlPayload));
    if (telemetryPayload) this.safePushEvent("remote_control", telemetryPayload);
  },

  handleKeyPress(event) {
    // Ignore events that shouldn't be handled (e.g. when typing in input fields)
    if (shouldIgnoreKeyEvent(event)) {
      return;
    }

    if (this.keyboardPassThrough) {
      event.preventDefault();
      event.stopPropagation();
      if (this.remoteControlReady()) {
        if (event.type === "keydown") {
          // only throttle keydown events
          this.sendKeyboardPassThroughEventThrottled(event);
        } else if (event.type === "keyup") {
          this.sendRemoteControl(REMOTE_CONTROL_KEYBOARD_PASS_THROUGH, event, event.repeat);
        }
      }
      return;
    }

    const keyMap = {
      m: "toggle_mute",
      t: "toggle_theater_mode",
      c: "screen_capture",
      k: "toggle_keyboard",
      F2: "toggle_stats",
      F3: "toggle_remote",
      F6: "toggle_camera_panel",
      F7: "live_calibrate",
      "~": "toggle_editor",
      "?": "show_remote_help",
    };

    // F5 is browser reload. Only staff sessions claim it, and only on the
    // full access screen (the attribute is absent in video chrome).
    if (this.el.dataset.adminShortcuts === "true") {
      keyMap.F5 = "toggle_admin_menu";
    }

    if (event.type === "keydown") {
      const eventName = keyMap[event.key];
      const withModifier = event.metaKey || event.ctrlKey || event.altKey;

      if (!withModifier && eventName) {
        event.preventDefault();
        if (!event.repeat) {
          this.safePushEvent(eventName, {});
        }
        return;
      }
    }

    this.handleRemoteKeyPress(event);
  },

  handleRemoteKeyPress(event) {
    const command = LOOKUP[event.key];

    if (command) {
      event.preventDefault();

      // Highlight every visible remote button bound to this command (virtual remote, mini-remote strip, mobile d-pad, etc.).
      const $els = document.querySelectorAll(
        "[data-remote-action=" + command + "][data-active-class-name]",
      );

      if (event.type === "keydown") {
        this.sendKeyPressEventThrottled(event, command);

        if (event.repeat === false) {
          $els.forEach(($el) => addActiveClasses($el));
        }
      } else if (event.type === "keyup") {
        if (this.longPressSupport) {
          // if long press is supported, we should just forward the keyup event
          // to `sendKeyPressEventThrottled` to maintain correct repeat logic
          this.sendKeyPressEventThrottled(event, command);
        } else {
          // if long press is not supported, we just send keyup directly
          // to sendRemoteControl
          this.sendRemoteControl(command, event, event.repeat);
        }

        $els.forEach(($el) => {
          setTimeout(() => removeActiveClasses($el), 100);
        });
      }
    }
  },

  handleExecuteSnippet() {
    console.log("CMD+Enter triggered from code editor");
    this.safePushEvent("execute_snippet", {});
  },

  handleRemoteButtonPress(message) {
    const { command, event, repeat } = message.detail;

    this.sendRemoteControl(command, { type: event }, repeat);
  },

  // Forwards a captured voice utterance to the sidecar over the existing
  // RTCDataChannel as voice_start/voice_chunk/voice_end messages.
  handleVoiceControl(message) {
    const { action, data } = message.detail;

    if (action === "start") {
      this.sendVoiceControl({ type: "voice_start" });
    } else if (action === "chunk") {
      this.sendVoiceControl({ type: "voice_chunk", data });
    } else if (action === "end") {
      this.sendVoiceControl({ type: "voice_end" });
    }
  },

  sendVoiceControl(payload) {
    if (!this.remoteControlReady()) return;

    this.remoteControl.send(JSON.stringify(payload));
  },

  handleVideoPlaying() {
    console.log("Content is playing");

    this.safePushEvent("connection:changed", {
      state: "playing",
    });

    // Report resolution once video starts playing and dimensions are available
    if (this.videoElement.videoWidth && this.videoElement.videoHeight) {
      this.safePushEvent("resolution_updated", {
        width: this.videoElement.videoWidth,
        height: this.videoElement.videoHeight,
      });
    }
  },

  handleScreenTap(event) {
    if (this.pointerControl) {
      if (this.pointerLocked()) {
        this.flushPointer();
        this.sendPointerCommand("pointer_click");
      } else {
        this.videoElement.requestPointerLock?.();
      }
      return;
    }

    // If we were swiping, don't register a tap
    if (this.isSwiping) {
      this.isSwiping = false;
      return;
    }

    // TVs and STBs don't support direct screen taps. Pulse a focus ring on
    // each d-pad button to point the user at the on-screen navigation
    // affordance, and skip sending a tap to the device entirely.
    const deviceType = this.el.dataset.deviceType;
    if (deviceType === "tv" || deviceType === "stb") {
      for (const id of [
        "tv-dpad-up",
        "tv-dpad-left",
        "tv-dpad-ok",
        "tv-dpad-right",
        "tv-dpad-down",
      ]) {
        retriggerAnimation(id, "animate-dpad-button-pulse");
      }
      return;
    }

    const videoRect = event.target.getBoundingClientRect();
    const x = event.clientX - videoRect.left;
    const y = event.clientY - videoRect.top;
    const normalizedX = x / videoRect.width;
    const normalizedY = y / videoRect.height;

    console.log("Screen tap:", { x: normalizedX, y: normalizedY });

    if (this.remoteControlReady()) {
      this.remoteControl.send(
        JSON.stringify({
          type: "remote_control",
          command: "tap",
          event: "click",
          repeat: false,
          coords: { x: normalizedX, y: normalizedY },
        }),
      );

      this.safePushEvent("remote_control", {
        command: `tap ${normalizedX} ${normalizedY}`,
        event: "click",
        repeat: false,
        client_timestamp: performance.now(),
      });
    }
  },

  handleMouseDown(event) {
    // Prevent default to avoid text selection during swipe
    event.preventDefault();

    if (this.pointerLocked()) {
      if (event.button === 2 && this.pointerRightClick) {
        this.flushPointer();
        this.sendPointerCommand("pointer_right_click");
      }
      return;
    }

    const videoRect = event.target.getBoundingClientRect();

    this.isMouseDown = true;
    this.touchStartX = (event.clientX - videoRect.left) / videoRect.width;
    this.touchStartY = (event.clientY - videoRect.top) / videoRect.height;
    this.touchStartTime = performance.now();
    this.isSwiping = false;
  },

  handleMouseMove(event) {
    if (this.pointerLocked()) {
      this.pointerDeltaX += event.movementX;
      this.pointerDeltaY += event.movementY;
      return;
    }

    if (!this.isMouseDown) return;

    // Mark as swiping if moved more than a small threshold
    const videoRect = event.target.getBoundingClientRect();
    const currentX = (event.clientX - videoRect.left) / videoRect.width;
    const currentY = (event.clientY - videoRect.top) / videoRect.height;

    const deltaX = Math.abs(currentX - this.touchStartX);
    const deltaY = Math.abs(currentY - this.touchStartY);

    if (deltaX > 0.03 || deltaY > 0.03) {
      this.isSwiping = true;
      // Prevent click event from firing when we're swiping
      event.preventDefault();
      event.stopPropagation();
    }
  },

  handleMouseUp(event) {
    if (!this.isMouseDown) return;

    this.isMouseDown = false;

    if (!this.isSwiping) return; // Was a click, not a swipe

    const videoRect = event.target.getBoundingClientRect();

    const endX = (event.clientX - videoRect.left) / videoRect.width;
    const endY = (event.clientY - videoRect.top) / videoRect.height;
    const duration = Math.min(performance.now() - this.touchStartTime, 1000);

    console.log("Swipe:", {
      startX: this.touchStartX,
      startY: this.touchStartY,
      endX,
      endY,
      duration,
    });

    if (this.remoteControlReady()) {
      this.remoteControl.send(
        JSON.stringify({
          type: "remote_control",
          command: "swipe",
          event: "swipe",
          coords: {
            startX: this.touchStartX,
            startY: this.touchStartY,
            endX,
            endY,
            duration,
          },
        }),
      );

      this.safePushEvent("remote_control", {
        command: `swipe ${this.touchStartX} ${this.touchStartY} ${endX} ${endY} ${duration}`,
        event: "swipe",
        repeat: false,
        client_timestamp: performance.now(),
      });
    }

    // Prevent the click event from firing
    event.preventDefault();
    event.stopPropagation();
  },

  handleWheel(event) {
    if (!this.pointerLocked()) return;

    event.preventDefault();
    if (this.pointerScroll) this.pointerScrollY += event.deltaY;
  },

  handleContextMenu(event) {
    if (this.pointerLocked()) event.preventDefault();
  },

  pointerLocked() {
    return document.pointerLockElement === this.videoElement;
  },

  handlePointerLockChange() {
    if (this.pointerLocked()) {
      this.js().setAttribute(this.el, "data-pointer-locked", "");
      this.pointerFlushTimer ??= setInterval(this.flushPointer, POINTER_FLUSH_INTERVAL_MS);
    } else {
      this.js().removeAttribute(this.el, "data-pointer-locked");
      clearInterval(this.pointerFlushTimer);
      this.pointerFlushTimer = null;
      this.flushPointer();
    }
  },

  // Movement deltas are fractional on high-DPI screens. Send whole pixels and
  // carry the remainder into the next flush so slow drags still add up.
  flushPointer() {
    const dx = Math.trunc(this.pointerDeltaX);
    const dy = Math.trunc(this.pointerDeltaY);
    const scroll = Math.trunc(this.pointerScrollY);

    this.pointerDeltaX -= dx;
    this.pointerDeltaY -= dy;
    this.pointerScrollY -= scroll;

    if (dx !== 0 || dy !== 0) this.sendPointerCommand(`pointer_move ${dx} ${dy}`);
    if (scroll !== 0) this.sendPointerCommand(`pointer_scroll ${scroll}`);
  },

  sendPointerCommand(command) {
    if (!this.remoteControlReady()) return;

    this.remoteControl.send(JSON.stringify({ type: "remote_control", command }));
  },

  // pushEvent rejects when the LiveView socket is momentarily disconnected.
  // None of this hook's events are worth retrying on that failure, so every
  // call site uses this instead of calling pushEvent directly.
  safePushEvent(event, payload) {
    return Promise.resolve(this.pushEvent(event, payload)).catch((err) => {
      console.warn(`safePushEvent(${event}) failed`, err);
    });
  },

  writeStats(stats) {
    this.safePushEvent("webrtc-stats", { stats });
  },

  destroyed() {
    console.log("Closing RTCPeerConnection");

    if (this.metrics) {
      this.metrics.destroy();
      this.metrics = null;
    }

    this.devtools.stop();

    if (this.peer) {
      this.peer.close();
    }

    this.videoElement.removeEventListener("playing", this.handleVideoPlaying);
    this.removeServerEventHandlers?.();
    this.removeEventListeners?.();
    this.removeAudioUnlock?.();
  },

  updateAudio(volume) {
    this.desiredVolume = volume;
    this.applyAudioState();
  },

  applyAudioState() {
    const audio = this.audioElement;

    if (
      !audio ||
      !this.warmedUp ||
      this.desiredVolume === null ||
      this.desiredVolume === undefined ||
      !audio.srcObject
    ) {
      return;
    }

    const volume = this.desiredVolume;
    audio.volume = volume / 100;

    // An explicit mute from the user (volume 0) always wins.
    if (volume === 0) {
      audio.muted = true;
      return;
    }

    // A gesture already unlocked audio, or we confirmed audible playback earlier.
    if (this.audioUnlocked) {
      audio.muted = false;
      return;
    }

    // Browsers that enforce the autoplay policy and report it up front
    // (Firefox 120+, Safari 17.4+): stay muted and wait for a gesture, with no
    // failed play() attempt and no console warning.
    if (
      typeof navigator.getAutoplayPolicy === "function" &&
      navigator.getAutoplayPolicy("mediaelement") !== "allowed"
    ) {
      audio.muted = true;
      this.waitForGestureToEnableAudio();
      return;
    }

    // Otherwise (e.g. Chrome) audible playback is generally allowed for a live
    // stream, so play with sound immediately. The catch is a safety net: if a
    // browser blocks it anyway, fall back to muted-until-gesture.
    audio.muted = false;
    audio.play().then(
      () => {
        this.audioUnlocked = true;
      },
      (err) => {
        if (err?.name !== "NotAllowedError") {
          console.warn("Audio playback failed", err);
          return;
        }
        audio.muted = true;
        audio.play().catch((err) => {
          console.warn("Audio playback failed", err);
        });

        this.waitForGestureToEnableAudio();
      },
    );
  },

  waitForGestureToEnableAudio() {
    if (this.removeAudioUnlock) return; // already waiting

    const unlock = () => {
      if (this.audioUnlocked) return;

      console.log("User gesture detected, enabling audio");
      this.audioUnlocked = true;

      this.applyAudioState();

      this.audioElement?.play().catch((err) => {
        console.warn("Audio playback failed", err);
      });

      this.removeAudioUnlock?.();
    };

    this.removeAudioUnlock = () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);

      this.removeAudioUnlock = null;
    };

    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
  },
};
